import {
  AbilityBuilder,
  type MatchConditions,
  PureAbility,
} from '@casl/ability';

import {
  UserRole,
  UserType,
  Resource,
  ACTIONS,
} from '../../constants/constants';
import { UserTeam } from '../../hooks/useUserTeam';

export type Subjects = 'all' | { teamId: string } | Resource;

export type TAppAbility = PureAbility<[ACTIONS, Subjects], MatchConditions>;
const lambdaMatcher = (matchConditions: MatchConditions): MatchConditions => {
  return matchConditions;
};
export function defineAbilitiesFor(user: UserTeam | null): TAppAbility {
  const { can, cannot, build } = new AbilityBuilder<TAppAbility>(PureAbility);

  if (!user) {
    return build({ conditionsMatcher: lambdaMatcher });
  }

  // Allow all users to access the About page

  const { role, userType } = user;
  if (userType === UserType.Internal) {
    if (role === UserRole.PARTNER) {
      can(ACTIONS.MANAGE, 'all');
    } else if (role === UserRole.ACCOUNT_MANAGER || role === UserRole.MANAGER) {
      can(ACTIONS.READ, Resource.Analytics);
      can(ACTIONS.READ, Resource.Settings);
      can(ACTIONS.READ, Resource.CheckInUsage);
      can(ACTIONS.UPDATE, Resource.Settings, ({ teamId }: { teamId: string }) =>
        user?.accessibleTeams?.some((team) => team?.id === teamId),
      );
      can(ACTIONS.READ, Resource.YourTeams, (subject: { teamId: string }) =>
        user?.accessibleTeams?.some((team) => team?.id === subject.teamId),
      );
      cannot(ACTIONS.READ, Resource.Dashboard);
      cannot(ACTIONS.READ, Resource.CheckInQuality);
    } else if (role === UserRole.EMPLOYEE || role === UserRole.SHADOW) {
      can(ACTIONS.READ, Resource.Settings);
      can(ACTIONS.READ, Resource.Analytics);
      can(ACTIONS.READ, Resource.CheckInUsage);
      cannot(ACTIONS.READ, Resource.CheckInQuality);
      cannot(ACTIONS.UPDATE, Resource.Settings);
      can(ACTIONS.READ, Resource.YourTeams, (subject: { teamId: string }) =>
        user?.accessibleTeams?.some((team) => team?.id === subject.teamId),
      );
      cannot(ACTIONS.READ, Resource.Dashboard);
    }
    can(ACTIONS.CREATE, Resource.CheckIn, () => user.isTeamMember);
    can(ACTIONS.READ, Resource.PerformanceReview);
    can(ACTIONS.READ, Resource.Reports);
    can(ACTIONS.READ, Resource.GiveFeedback);
  } else if (userType === UserType.External) {
    cannot(ACTIONS.CREATE, Resource.CheckIn);
    cannot(ACTIONS.READ, Resource.Settings);
    cannot(ACTIONS.READ, Resource.Analytics);
    cannot(ACTIONS.READ, Resource.CheckInQuality);
    cannot(ACTIONS.READ, Resource.CheckInUsage);
    cannot(ACTIONS.UPDATE, Resource.Settings);
    can(ACTIONS.READ, Resource.YourTeams, (subject: { teamId: string }) =>
      user?.accessibleTeams?.some((team) => team?.id === subject.teamId),
    );
    can(ACTIONS.READ, Resource.Dashboard);
    cannot(ACTIONS.READ, Resource.PerformanceReview);
    cannot(ACTIONS.READ, Resource.GiveFeedback);
    cannot(ACTIONS.READ, Resource.Reports);
  } else {
    cannot(ACTIONS.MANAGE, 'all');
  }

  return build({ conditionsMatcher: lambdaMatcher });
}
